import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useChat } from '../context/ChatContext';
import { useIdentity, personLabel, isRep, repSlug, nearestRep } from '../context/IdentityContext';
import { getNameSuggestions, listEmailDrafts, getInboxSummary } from '../services/api';
import QuickTaskComposer from './QuickTaskComposer';
import { Task } from '../types';

interface NavbarProps {
  onLogout?: () => void;
}

interface NavItem {
  path: string;
  label: string;
  color: string;
  // Count shown as a pill on the item and rolled up onto its group button. Used for
  // the Approval Queue, which was invisible until someone opened the right dropdown:
  // on 2026-09-08 a filed draft sat unapproved because nobody could find the page.
  badge?: number;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  // Optional one-line orientation shown at the top of the group dropdown.
  note?: string;
}

const Navbar: React.FC<NavbarProps> = ({ onLogout }) => {
  const { isDark, toggleTheme, theme } = useTheme();
  const { toggleDrawer } = useChat();
  const { currentPerson, setCurrentPerson, recentPeople, peopleNames } = useIdentity();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Header quick-task shortcut: a small "+" that opens the SHARED QuickTaskComposer
  // from ANY page, so there's no separate form to drift and no hunting through the
  // Development dropdown -> Quick Tasks -> Create.
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  // Free-form identity combobox: what's typed in the box, plus the
  // task-creation name source (same /name-suggestions endpoint CreateTask uses).
  const [identityQuery, setIdentityQuery] = useState('');
  const [taskNameSuggestions, setTaskNameSuggestions] = useState<string[]>([]);
  // Near-miss guard: when a typed name looks like a slightly-off rep name (typo /
  // "Subham K"), we DON'T silently commit it as a non-rep free-form identity (which
  // would strand the user out of their lane). Instead we surface a "did you mean …"
  // confirm and keep the prior valid identity until the user picks clearly.
  const [nearMiss, setNearMiss] = useState<{ typed: string; rep: string } | null>(null);
  // Drafts waiting on a human, polled so the count is live while the nav stays open.
  const [waitingDrafts, setWaitingDrafts] = useState(0);
  // Unseen inbox responses (campaign replies + godswill@ mail), same polling pattern.
  const [unseenResponses, setUnseenResponses] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);
  const identityInputRef = useRef<HTMLInputElement>(null);

  // Approval Queue badge: count drafts still needing a person (pending, approved but
  // unsent, failed) so the nav itself says there is something to approve.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      listEmailDrafts()
        .then((res) => {
          if (cancelled) return;
          const drafts = Array.isArray(res?.drafts) ? res.drafts : [];
          setWaitingDrafts(
            drafts.filter(
              (d) => d.status === 'pending_review' || d.status === 'approved' || d.status === 'failed'
            ).length
          );
        })
        .catch(() => {
          /* the badge is informational; a failed poll just leaves the last count */
        });
    };
    refresh();
    const id = window.setInterval(refresh, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Email Responses badge: unseen count from the inbox summary (support@ + godswill@
  // combined). Same polling shape as the Approval Queue badge above.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      getInboxSummary()
        .then((summary) => {
          if (cancelled) return;
          setUnseenResponses(summary?.total_unseen || 0);
        })
        .catch(() => {
          /* the badge is informational; a failed poll just leaves the last count */
        });
    };
    refresh();
    const id = window.setInterval(refresh, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Pull suggestions from the SAME source task creation uses (CreateTask ->
  // getNameSuggestions -> GET /name-suggestions). Debounced; fed by what's typed.
  useEffect(() => {
    if (!identityOpen) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      getNameSuggestions(identityQuery.trim())
        .then((names) => {
          if (!cancelled) setTaskNameSuggestions(Array.isArray(names) ? names : []);
        })
        .catch(() => {
          if (!cancelled) setTaskNameSuggestions([]);
        });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [identityOpen, identityQuery]);

  // Merge suggestions: (a) everyone on the maintained people list (the AI reps first,
  // then every roster account, so a teammate who just signed up with their
  // @spiritualdata.org address is offered here), (b) recent users, (c) task-creation
  // name source. De-dupe case-insensitively, filter by what's typed, cap the list.
  const identitySuggestions = useMemo(() => {
    const q = identityQuery.trim().toLowerCase();
    const out: string[] = [];
    const seen = new Set<string>();
    const add = (name: string) => {
      const n = (name || '').trim();
      if (!n) return;
      const key = n.toLowerCase();
      if (seen.has(key)) return;
      if (q && !key.includes(q)) return;
      seen.add(key);
      out.push(n);
    };
    peopleNames.forEach(add);
    recentPeople.forEach(add);
    taskNameSuggestions.forEach(add);
    return out.slice(0, 10);
  }, [identityQuery, peopleNames, recentPeople, taskNameSuggestions]);

  // Commit a chosen / typed identity. Reps are stored title-cased (e.g. "Joshua")
  // so created_by / uploaded_by fields read naturally. All slug-matching code already
  // lowercases before comparing, so lane wiring and attribution keep working.
  const commitNow = (raw: string) => {
    const name = (raw || '').trim();
    if (!name) return;
    const slug = repSlug(name);
    setCurrentPerson(slug ? personLabel(slug) : name);
    setIdentityQuery('');
    setNearMiss(null);
    setIdentityOpen(false);
  };

  // Commit a chosen / typed identity. If the typed name is a NEAR-MISS of a rep
  // (typo, or "Subham K"), don't strand the user as a free-form non-rep identity —
  // surface a "did you mean …" confirm and leave the current identity unchanged
  // until they pick. Exact reps and clearly-non-rep names commit straight through.
  const commitIdentity = (raw: string) => {
    const name = (raw || '').trim();
    if (!name) return;
    if (!isRep(name)) {
      const rep = nearestRep(name);
      if (rep) {
        setNearMiss({ typed: name, rep });
        return; // hold: prior valid identity stays until the user chooses
      }
    }
    commitNow(name);
  };

  // Open the shared composer from the header / mobile menu.
  const openQuickTask = () => {
    setQuickTaskOpen(true);
    setMobileMenuOpen(false);
  };

  // After the shared composer creates the task, jump to its live view on the
  // Quick Tasks page.
  const handleQuickTaskSubmitted = (task: Task) => {
    setQuickTaskOpen(false);
    navigate(`/quicktasks?task=${task.id}`);
  };

  // Three groups per Joshua's request: Development | Productivity | Admin.
  const groups: NavGroup[] = [
    {
      label: 'Development',
      note: 'Tasks = work you send off to run in the background. Quick Tasks = live, fire-and-watch.',
      items: [
        { path: '/tasks', label: 'Tasks', color: theme.primary },
        { path: '/quicktasks', label: 'Quick Tasks', color: '#f59e0b' },
        { path: '/', label: 'Features', color: '#10b981' },
        { path: '/releases', label: 'Releases', color: '#10b981' },
        { path: '/ideas', label: 'Product Ideas', color: '#8b5cf6' },
      ],
    },
    {
      label: 'Productivity',
      items: [
        { path: '/people', label: 'Command Center', color: '#8b5cf6' },
        { path: '/ai-representatives', label: 'My AI Representatives', color: '#8b5cf6' },
        { path: '/team-ai-representatives', label: 'Team AI Representatives', color: '#8b5cf6' },
        { path: '/approval-queue', label: 'Approval Queue', color: '#06b6d4', badge: waitingDrafts },
        { path: '/campaigns', label: 'Email Campaigns', color: '#06b6d4' },
        { path: '/responses', label: 'Email Responses', color: '#06b6d4', badge: unseenResponses },
        { path: '/reports', label: 'Team Reports', color: '#06b6d4' },
        { path: '/initiatives', label: 'Initiatives / Content', color: '#10b981' },
        { path: '/chat', label: 'Chat', color: '#8b5cf6' },
        { path: '/uploads', label: 'Uploads (file drop)', color: '#06b6d4' },
        { path: '/dependencies', label: 'Dependencies', color: '#f59e0b' },
        { path: '/dependencies?view=integrations', label: 'Integrations', color: '#f59e0b' },
      ],
    },
    {
      label: 'Admin',
      items: [
        { path: '/analytics', label: 'Analytics', color: theme.primary },
        { path: '/management', label: 'Management', color: theme.primary },
        { path: '/access', label: 'Access (who can sign in)', color: '#ef4444' },
      ],
    },
  ];

  const isActive = (path: string) => {
    const base = path.split('?')[0];
    if (base === '/') return location.pathname === '/';
    return location.pathname.startsWith(base);
  };

  const groupActive = (group: NavGroup) => group.items.some((i) => isActive(i.path));

  // Roll every item badge in a group up onto its collapsed group button.
  const groupBadge = (group: NavGroup) =>
    group.items.reduce((sum, i) => sum + (i.badge || 0), 0);

  const badgePill = (count: number, onAccent: boolean): React.ReactElement => (
    <span
      style={{
        marginLeft: '6px',
        minWidth: '18px',
        padding: '1px 6px',
        borderRadius: '999px',
        backgroundColor: onAccent ? 'rgba(255,255,255,0.9)' : '#ef4444',
        color: onAccent ? '#111' : '#fff',
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: '16px',
        display: 'inline-block',
        textAlign: 'center',
      }}
    >
      {count}
    </span>
  );

  // Close dropdowns on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenGroup(null);
        setIdentityOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close dropdowns on navigation.
  useEffect(() => {
    setOpenGroup(null);
    setIdentityOpen(false);
    setNearMiss(null);
  }, [location.pathname, location.search]);

  const dropdownItemStyle = (active: boolean, color: string): React.CSSProperties => ({
    display: 'block',
    padding: '10px 14px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    backgroundColor: active ? color : 'transparent',
    color: active ? '#fff' : theme.text,
    whiteSpace: 'nowrap',
  });

  return (
    <>
      <nav
        ref={navRef}
        style={{
          backgroundColor: theme.surface,
          borderBottom: `1px solid ${theme.border}`,
          padding: '0 16px',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 300,
          maxWidth: '100vw',
          overflowX: 'visible',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '1400px',
            margin: '0 auto',
            height: '56px',
          }}
        >
          {/* Logo / Title */}
          <Link
            to="/"
            style={{
              textDecoration: 'none',
              color: theme.text,
              fontWeight: '700',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '24px' }}>🤖</span>
            <span className="nav-title">Command Center</span>
          </Link>

          {/* Desktop Navigation: grouped dropdowns */}
          <div
            className="desktop-nav"
            style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
          >
            {/* Header quick-task shortcut: fire a quick task from anywhere, no
                dropdown digging. Uses the Quick Tasks amber so it reads as "quick". */}
            <button
              onClick={openQuickTask}
              title="New quick task"
              aria-label="New quick task"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#f59e0b',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '20px',
                fontWeight: 700,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '6px',
              }}
            >
              +
            </button>

            {groups.map((group) => {
              const open = openGroup === group.label;
              const active = groupActive(group);
              return (
                <div key={group.label} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setOpenGroup(open ? null : group.label)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      backgroundColor: active ? theme.surfaceHover : 'transparent',
                      color: active ? theme.primary : theme.text,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {group.label}
                    {groupBadge(group) > 0 && badgePill(groupBadge(group), false)}
                    <span style={{ fontSize: '10px' }}>▾</span>
                  </button>
                  {open && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '4px',
                        backgroundColor: theme.surface,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                        minWidth: '200px',
                        overflow: 'hidden',
                        zIndex: 310,
                      }}
                    >
                      {group.note && (
                        <div
                          style={{
                            padding: '10px 14px',
                            fontSize: '12px',
                            color: theme.textSecondary,
                            borderBottom: `1px solid ${theme.border}`,
                            lineHeight: 1.4,
                            whiteSpace: 'normal',
                          }}
                        >
                          {group.note}
                        </div>
                      )}
                      {group.items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          style={{
                            ...dropdownItemStyle(isActive(item.path), item.color),
                            borderBottom: `1px solid ${theme.border}`,
                          }}
                        >
                          {item.label}
                          {item.badge ? badgePill(item.badge, isActive(item.path)) : null}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Global identity switcher: a combobox — type ANY name, or pick a
                rep / recent / task-creation suggestion. Always visible. */}
            <div style={{ position: 'relative', marginLeft: '8px' }}>
              <button
                onClick={() => {
                  setIdentityOpen((v) => {
                    const next = !v;
                    if (next) {
                      setIdentityQuery('');
                      setTimeout(() => identityInputRef.current?.focus(), 0);
                    }
                    return next;
                  });
                }}
                title="Who you are — type any name; used across every command-center feature"
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.primary}`,
                  backgroundColor: theme.primary,
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>You are:</span>
                <span>{personLabel(currentPerson)}</span>
                <span style={{ fontSize: '10px' }}>▾</span>
              </button>
              {identityOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: theme.surface,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                    width: '240px',
                    overflow: 'hidden',
                    zIndex: 310,
                  }}
                >
                  <div style={{ padding: '8px' }}>
                    <input
                      ref={identityInputRef}
                      type="text"
                      value={identityQuery}
                      onChange={(e) => {
                        setIdentityQuery(e.target.value);
                        setNearMiss(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Enter commits the first suggestion if the box is empty,
                          // otherwise the typed text (any free-form name).
                          commitIdentity(identityQuery.trim() || identitySuggestions[0] || '');
                        } else if (e.key === 'Escape') {
                          setIdentityOpen(false);
                        }
                      }}
                      placeholder="Type your name…"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${theme.border}`,
                        backgroundColor: theme.background,
                        color: theme.text,
                        fontSize: '14px',
                      }}
                    />
                  </div>
                  {/* Near-miss guard: a typed name that looks like a slightly-off
                      rep name. Confirm the rep (keeps you in your lane) or keep the
                      free-form name explicitly — never strand silently. */}
                  {nearMiss && (
                    <div
                      style={{
                        margin: '0 8px 8px',
                        padding: '10px',
                        borderRadius: '6px',
                        border: `1px solid ${theme.primary}`,
                        backgroundColor: theme.primary + '12',
                      }}
                    >
                      <div style={{ color: theme.text, fontSize: '13px', marginBottom: '8px' }}>
                        Did you mean <strong>{personLabel(nearMiss.rep)}</strong> (a rep with their own lane)?
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => commitNow(nearMiss.rep)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: theme.primary,
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          Use {personLabel(nearMiss.rep)}
                        </button>
                        <button
                          onClick={() => commitNow(nearMiss.typed)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: 'transparent',
                            color: theme.text,
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          Keep “{nearMiss.typed}”
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                    {identityQuery.trim() && identitySuggestions.length === 0 && (
                      <button
                        onClick={() => commitIdentity(identityQuery)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 14px',
                          border: 'none',
                          borderTop: `1px solid ${theme.border}`,
                          backgroundColor: 'transparent',
                          color: theme.text,
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                      >
                        Use “{identityQuery.trim()}”
                      </button>
                    )}
                    {identityQuery.trim() && identitySuggestions.map((name) => {
                      const active = name.toLowerCase() === currentPerson.trim().toLowerCase();
                      return (
                        <button
                          key={name}
                          onClick={() => commitIdentity(name)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            textAlign: 'left',
                            padding: '10px 14px',
                            border: 'none',
                            borderTop: `1px solid ${theme.border}`,
                            backgroundColor: active ? theme.primary : 'transparent',
                            color: active ? '#fff' : theme.text,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: active ? 700 : 500,
                          }}
                        >
                          <span>{personLabel(name)}</span>
                          {isRep(name) && (
                            <span
                              style={{
                                fontSize: '10px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                color: active ? '#fff' : theme.textSecondary,
                              }}
                            >
                              rep
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {!identityQuery.trim() && (
                      <button
                        onClick={() => {}}
                        disabled
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 14px',
                          border: 'none',
                          borderTop: `1px solid ${theme.border}`,
                          backgroundColor: theme.primary,
                          color: '#fff',
                          cursor: 'default',
                          fontSize: '14px',
                          fontWeight: 600,
                        }}
                      >
                        {personLabel(currentPerson)}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Chat pop-out drawer toggle (always visible). */}
            <button
              onClick={toggleDrawer}
              style={{
                padding: '8px 12px',
                backgroundColor: 'transparent',
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                marginLeft: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              title="Open chat panel"
            >
              <span>💬</span>
              <span>Chat</span>
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              style={{
                padding: '8px 12px',
                backgroundColor: 'transparent',
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                marginLeft: '6px',
              }}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? '☀️' : '🌙'}
            </button>

            {/* Logout */}
            {onLogout && (
              <button
                onClick={onLogout}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginLeft: '4px',
                }}
              >
                Logout
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              padding: '8px',
              backgroundColor: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '20px',
              color: theme.text,
            }}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile Menu: grouped, with identity switcher + chat toggle */}
        {mobileMenuOpen && (
          <div
            className="mobile-menu"
            style={{
              padding: '12px 0',
              borderTop: `1px solid ${theme.border}`,
              maxHeight: 'calc(100vh - 56px)',
              overflowY: 'auto',
            }}
          >
            {/* Quick-task shortcut (mobile). */}
            <div style={{ padding: '0 16px 12px' }}>
              <button
                onClick={openQuickTask}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#f59e0b',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '20px', lineHeight: 1 }}>+</span>
                New Quick Task
              </button>
            </div>

            {/* Identity switcher (mobile): free-form name input + rep / recent quick-picks. */}
            <div style={{ padding: '0 16px 12px' }}>
              <div style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
                You are — <span style={{ textTransform: 'none', fontWeight: 500 }}>{personLabel(currentPerson)}</span>
              </div>
              <input
                type="text"
                value={identityQuery}
                onChange={(e) => {
                  setIdentityQuery(e.target.value);
                  setNearMiss(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitIdentity(identityQuery);
                  }
                }}
                placeholder="Type your name and press Enter…"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
              {nearMiss && (
                <div
                  style={{
                    margin: '0 0 8px',
                    padding: '10px',
                    borderRadius: '6px',
                    border: `1px solid ${theme.primary}`,
                    backgroundColor: theme.primary + '12',
                  }}
                >
                  <div style={{ color: theme.text, fontSize: '13px', marginBottom: '8px' }}>
                    Did you mean <strong>{personLabel(nearMiss.rep)}</strong> (a rep with their own lane)?
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => commitNow(nearMiss.rep)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: theme.primary,
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Use {personLabel(nearMiss.rep)}
                    </button>
                    <button
                      onClick={() => commitNow(nearMiss.typed)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: `1px solid ${theme.border}`,
                        backgroundColor: 'transparent',
                        color: theme.text,
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Keep “{nearMiss.typed}”
                    </button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {}}
                  disabled
                  style={{
                    padding: '8px 12px',
                    borderRadius: '999px',
                    border: `1px solid ${theme.primary}`,
                    backgroundColor: theme.primary,
                    color: '#fff',
                    cursor: 'default',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {personLabel(currentPerson)}
                </button>
              </div>
            </div>

            {groups.map((group) => {
              const open = openGroup === group.label;
              const active = groupActive(group);
              return (
                <div key={group.label} style={{ marginBottom: '4px' }}>
                  <button
                    onClick={() => setOpenGroup(open ? null : group.label)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      color: active ? theme.primary : theme.textSecondary,
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    <span>
                      {group.label}
                      {groupBadge(group) > 0 && badgePill(groupBadge(group), false)}
                    </span>
                    <span style={{ fontSize: '10px' }}>{open ? '▾' : '▸'}</span>
                  </button>
                  {open && group.note && (
                    <div
                      style={{
                        padding: '6px 16px 8px 28px',
                        fontSize: '12px',
                        color: theme.textSecondary,
                        lineHeight: 1.4,
                      }}
                    >
                      {group.note}
                    </div>
                  )}
                  {open &&
                    group.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        style={{
                          display: 'block',
                          padding: '10px 16px 10px 28px',
                          textDecoration: 'none',
                          fontSize: '15px',
                          fontWeight: 500,
                          backgroundColor: isActive(item.path) ? item.color : 'transparent',
                          color: isActive(item.path) ? '#fff' : theme.text,
                        }}
                      >
                        {item.label}
                        {item.badge ? badgePill(item.badge, isActive(item.path)) : null}
                      </Link>
                    ))}
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', padding: '0 16px' }}>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  toggleDrawer();
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: theme.surfaceHover,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                💬 Chat
              </button>
              <button
                onClick={toggleTheme}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: theme.surfaceHover,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {isDark ? '☀️ Light' : '🌙 Dark'}
              </button>

              {onLogout && (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout();
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Shared quick-task composer (same component the Quick Tasks page uses).
          Fired from the header / mobile +, it jumps to the new task's live view. */}
      <QuickTaskComposer
        open={quickTaskOpen}
        onClose={() => setQuickTaskOpen(false)}
        onSubmitted={handleQuickTaskSubmitted}
      />

      {/* CSS for responsive behavior */}
      <style>{`
        @media (max-width: 900px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .nav-title {
            display: none;
          }
        }

        @media (min-width: 901px) {
          .mobile-menu {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;
